import { AI_CHAT_LIMITS, SESSION_ID_PREFIX } from "@/constants/aiChat";
import { useAIChatDebugStore } from "@/stores/aiChatDebugStore";
import type { PendingTemplateIntercept } from "@/types/aiChat";
import type {
  AgentMode,
  ChatImageAttachment,
  ChatMessage,
  ChatSession,
  MessageToolCalls,
  StreamingNode,
  ToolCallStatus,
  ToolUtilizationScore,
} from "@/types/aiChat";
import { create } from "zustand";

const isAIChatStoreDebugEnabled = process.env.NODE_ENV === "development";
const aiChatStoreDebug = {
  log: (...args: unknown[]) => {
    if (isAIChatStoreDebugEnabled) {
      console.log(...args);
    }
  },
};

/**
 * AI Chat Store Interface
 *
 * Global state management for the AI chat feature using Zustand.
 * This store manages:
 * - Chat panel UI state (open/closed, minimized/maximized)
 * - Current chat session with messages
 * - Loading states for chat operations
 * - Error handling
 * - History tracking and retry functionality
 * - Streaming state for progressive node rendering
 */
interface AIChatStore {
  // Panel state
  isPanelOpen: boolean;
  isPanelMinimized: boolean;
  panelView: "chat" | "history";

  // Current session
  currentSession: ChatSession | null;

  // Loading and error states
  isLoading: boolean;
  isPreparing: boolean;
  isLoadingHistory: boolean;
  error: Error | null;

  // Last request for retry functionality
  lastRequest: {
    message: string;
    workflowName?: string;
    workflowDescription?: string;
  } | null;

  // Pending message to be sent when panel opens
  pendingMessage: string | null;

  // Monotonic counter incremented each time setPendingMessage is called with a
  // non-null value. The auto-send effect in AIChatStreamPanel uses this instead
  // of the message string to detect new messages, so re-clicking "Fix with Caddie"
  // with the same validation errors still triggers a send. See B3-3880.
  pendingMessageId: number;

  // Pending images to attach to the next message. Handed off from surfaces
  // outside the chat panel (e.g. the dashboard prompt input) alongside
  // pendingMessage; consumed + cleared by the auto-send effect in AIChatStreamPanel.
  pendingImages: ChatImageAttachment[];

  // Rich error block shown in chat UI but NOT sent to backend.
  // Used by "Fix with AI" to show structured error details to the user.
  pendingErrorBlock: string | null;

  // Canvas prefill: pre-fills the canvas AI input without submitting
  canvasPrefill: string | null;

  // Canvas submit override: when set, the canvas form calls this instead of opening the AI panel.
  // Intentionally non-serializable — transient override for onboarding prefill.
  // Do NOT add persist middleware without excluding this field.
  canvasSubmitOverride: ((message: string) => void) | null;

  // Streaming state
  isStreaming: boolean;
  streamingNodes: Map<string, StreamingNode>;
  streamingConversation: string;
  loadingNodeIds: Set<string>;
  thinkingMessages: string[];
  toolCalls: ToolCallStatus[];
  /** Accumulated tool calls from previous messages in this session */
  toolCallHistory: MessageToolCalls[];
  /** Braintrust trace URL for the current session (sent via SSE) */
  braintrustTraceUrl: string | null;
  /** Tool utilization scores for the current session */
  toolUtilization: ToolUtilizationScore[] | null;

  // Pending user input state (when AI asks questions or proposes a plan)
  hasPendingAskBlock: boolean;
  hasPendingPlanBlock: boolean;
  pendingTemplateIntercept: PendingTemplateIntercept | null;

  // Actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  minimizePanel: () => void;
  maximizePanel: () => void;
  setPanelView: (view: "chat" | "history") => void;

  // Session management
  startNewSession: (
    sessionId?: string,
    workflowId?: string,
    workflowName?: string,
    conversationIdOverride?: string,
  ) => void;
  setWorkflowContext: (
    workflowId: string | null,
    workflowName?: string | null,
    conversationIdOverride?: string,
  ) => void;
  setSessionContext: (sessionId: string | null) => void;
  clearSession: () => void;

  // Message management
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (messageId: string) => void;
  setMessages: (messages: ChatMessage[]) => void;

  // State management
  setLoading: (isLoading: boolean) => void;
  setPreparing: (isPreparing: boolean) => void;
  setLoadingHistory: (isLoadingHistory: boolean) => void;
  setError: (error: Error | null) => void;
  setLastRequest: (request: { message: string; workflowName?: string; workflowDescription?: string } | null) => void;
  setPendingMessage: (message: string | null) => void;
  setPendingImages: (images: ChatImageAttachment[]) => void;
  setPendingErrorBlock: (message: string | null) => void;
  setCanvasPrefill: (message: string | null) => void;
  setCanvasSubmitOverride: (fn: ((message: string) => void) | null) => void;

  // Streaming actions
  setStreaming: (isStreaming: boolean) => void;
  addStreamingNode: (nodeId: string, node: StreamingNode) => void;
  clearStreamingNodes: () => void;
  appendStreamingConversation: (chunk: string) => void;
  clearStreamingConversation: () => void;
  addLoadingNodeId: (nodeId: string) => void;
  removeLoadingNodeId: (nodeId: string) => void;
  clearLoadingNodeIds: () => void;
  addThinkingMessage: (message: string) => void;
  clearThinkingMessages: () => void;
  addToolCall: (toolCall: ToolCallStatus) => void;
  updateToolCall: (toolName: string, updates: Partial<ToolCallStatus>) => void;
  clearToolCalls: () => void;
  /** Archive current tool calls into history (called on stream completion) */
  archiveToolCallHistory: (userMessage: string) => void;
  setBraintrustTraceUrl: (url: string) => void;

  // Reconnection state
  requestId: string | null;
  lastEventIndex: number;
  isReconnecting: boolean;
  reconnectAttempt: number;

  // Reconnection actions
  setRequestId: (id: string | null) => void;
  updateLastEventIndex: (index: number) => void;
  setReconnecting: (isReconnecting: boolean, attempt?: number) => void;

  // Pending ask/plan block actions
  setHasPendingAskBlock: (hasPending: boolean) => void;
  setHasPendingPlanBlock: (hasPending: boolean) => void;
  setPendingTemplateIntercept: (intercept: PendingTemplateIntercept | null) => void;

  // Agent mode state
  /** Current mode the agent is operating in (updated by mode_switch stream events) */
  currentMode: AgentMode | null;
  /** Mode explicitly requested by the user via the mode selector UI */
  preferredMode: AgentMode | null;
  /** Last known mode per session key (workflowId or sessionId) — persists across history drawer views */
  sessionModes: Record<string, AgentMode>;
  setCurrentMode: (mode: AgentMode | null) => void;
  setPreferredMode: (mode: AgentMode | null) => void;

  // Reset
  reset: () => void;
}

/**
 * Generate a unique session ID for chat sessions
 * Format: session_{timestamp}_{random}
 */
function generateSessionId(): string {
  return `${SESSION_ID_PREFIX}${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Resolve the conversationId for a chat session.
 * Uses explicit override if provided, otherwise generates a new UUID.
 * Never uses workflowId as conversationId.
 */
function resolveConversationId(override?: string): string {
  return override || crypto.randomUUID();
}

const initialState = {
  isPanelOpen: false,
  isPanelMinimized: false,
  panelView: "chat" as const,
  currentSession: null,
  isLoading: false,
  isPreparing: false,
  isLoadingHistory: false,
  error: null,
  lastRequest: null,
  pendingMessage: null,
  pendingMessageId: 0,
  pendingImages: [] as ChatImageAttachment[],
  pendingErrorBlock: null,
  canvasPrefill: null,
  canvasSubmitOverride: null,
  // Streaming state
  isStreaming: false,
  streamingNodes: new Map<string, StreamingNode>(),
  streamingConversation: "",
  loadingNodeIds: new Set<string>(),
  thinkingMessages: [],
  toolCalls: [] as ToolCallStatus[],
  toolCallHistory: [] as MessageToolCalls[],
  braintrustTraceUrl: null,
  toolUtilization: null,
  // Pending user input state
  hasPendingAskBlock: false,
  hasPendingPlanBlock: false,
  pendingTemplateIntercept: null,
  // Agent mode state
  currentMode: null,
  preferredMode: null,
  sessionModes: {} as Record<string, AgentMode>,
  // Reconnection state
  requestId: null,
  lastEventIndex: -1,
  isReconnecting: false,
  reconnectAttempt: 0,
};

export const useAIChatStore = create<AIChatStore>((set, get) => ({
  ...initialState,

  // Panel actions
  // Note: WorkflowViewportController in WorkflowCanvas watches aiChatStore state
  // and handles fitView with proper sidebar offset, so we don't call fitView here
  openPanel: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Opening panel");
    set({ isPanelOpen: true, isPanelMinimized: false, panelView: "chat" });
  },

  closePanel: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Closing panel");
    set({ isPanelOpen: false, panelView: "chat" });
  },

  togglePanel: () => {
    const { isPanelOpen } = get();
    aiChatStoreDebug.log("🤖 [AI Store] Toggling panel:", !isPanelOpen);
    set({ isPanelOpen: !isPanelOpen, isPanelMinimized: false, panelView: "chat" });
  },

  minimizePanel: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Minimizing panel");
    set({ isPanelMinimized: true });
  },

  maximizePanel: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Maximizing panel");
    set({ isPanelMinimized: false, panelView: "chat" });
  },

  setPanelView: (view: "chat" | "history") => {
    set({ panelView: view });
  },

  // Session management
  startNewSession: (
    sessionId?: string,
    workflowId?: string,
    workflowName?: string,
    conversationIdOverride?: string,
  ) => {
    aiChatStoreDebug.log("🤖 [AI Store] Starting new session:", {
      sessionId,
      workflowId,
      workflowName,
      conversationIdOverride,
    });
    const now = new Date();
    // Resolve conversationId: explicit override > localStorage cache > new UUID.
    // Never use workflowId as conversationId — they are separate identifiers.
    const conversationId = resolveConversationId(conversationIdOverride);
    const session: ChatSession = {
      id: generateSessionId(),
      conversationId,
      sessionId: sessionId || null,
      workflowId: workflowId || null,
      workflowName: workflowName || null,
      messages: [],
      createdAt: now,
      updatedAt: now,
    };
    set({
      currentSession: session,
      error: null,
    });
  },

  setWorkflowContext: (workflowId: string | null, workflowName?: string | null, conversationIdOverride?: string) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting workflow context:", {
      workflowId,
      workflowName,
      conversationIdOverride,
    });
    const { currentSession } = get();
    if (currentSession) {
      // No debug data migration needed — conversationId is the stable key.
      // If conversationIdOverride is provided (from workflow record), adopt it.
      const conversationId = conversationIdOverride || currentSession.conversationId;
      set({
        currentSession: {
          ...currentSession,
          conversationId,
          workflowId,
          workflowName: workflowName || null,
          updatedAt: new Date(),
        },
      });
    } else {
      // Create new session if none exists.
      const conversationId = resolveConversationId(conversationIdOverride);
      const now = new Date();
      set({
        currentSession: {
          id: generateSessionId(),
          conversationId,
          sessionId: null,
          workflowId,
          workflowName: workflowName || null,
          messages: [],
          createdAt: now,
          updatedAt: now,
        },
      });
    }
  },

  setSessionContext: (sessionId: string | null) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting session context:", { sessionId });
    const { currentSession } = get();
    if (currentSession) {
      // Update existing session
      // Clear workflowId when setting sessionId (they are mutually exclusive)
      set({
        currentSession: {
          ...currentSession,
          sessionId,
          workflowId: null,
          workflowName: null,
          updatedAt: new Date(),
        },
      });
    } else {
      // Create new session if none exists
      const now = new Date();
      set({
        currentSession: {
          id: generateSessionId(),
          conversationId: crypto.randomUUID(),
          sessionId,
          workflowId: null,
          workflowName: null,
          messages: [],
          createdAt: now,
          updatedAt: now,
        },
      });
    }
  },

  clearSession: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Clearing session");
    set({
      currentSession: null,
      error: null,
      isPreparing: false,
      lastRequest: null,
      hasPendingAskBlock: false,
      hasPendingPlanBlock: false,
      pendingTemplateIntercept: null,
      currentMode: null,
      preferredMode: null,
      requestId: null,
      lastEventIndex: -1,
      isReconnecting: false,
      reconnectAttempt: 0,
      isLoadingHistory: false,
      panelView: "chat",
    });
  },

  // Message management
  addMessage: (message: ChatMessage) => {
    aiChatStoreDebug.log("🤖 [AI Store] Adding message:", message.id);
    const safeMessage = typeof message.content === "string" ? message : { ...message, content: "" };
    set(state => {
      // If no session exists, create one automatically
      if (!state.currentSession) {
        const now = new Date();
        return {
          currentSession: {
            id: generateSessionId(),
            conversationId: crypto.randomUUID(),
            sessionId: null,
            workflowId: null,
            workflowName: null,
            messages: [safeMessage],
            createdAt: now,
            updatedAt: now,
          },
        };
      }

      // Add message to existing session with bounds check
      let messages = [...state.currentSession.messages, safeMessage];

      // Prevent unbounded growth - keep only the most recent messages
      if (messages.length > AI_CHAT_LIMITS.MAX_MESSAGE_HISTORY) {
        messages = messages.slice(-AI_CHAT_LIMITS.MAX_MESSAGE_HISTORY);
      }

      return {
        currentSession: {
          ...state.currentSession,
          messages,
          updatedAt: new Date(),
        },
      };
    });
  },

  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => {
    aiChatStoreDebug.log("🤖 [AI Store] Updating message:", messageId);
    const safeUpdates =
      updates.content !== undefined && typeof updates.content !== "string" ? { ...updates, content: "" } : updates;
    set(state => ({
      currentSession: state.currentSession
        ? {
            ...state.currentSession,
            messages: state.currentSession.messages.map(m => {
              if (m.id !== messageId) return m;
              // When a loading bubble transitions to final, refresh its timestamp so
              // it sorts after any messages that arrived from other users while it was
              // loading (prevents the AI response from jumping backward in the list).
              const refreshTimestamp = safeUpdates.isLoading === false && m.isLoading;
              return { ...m, ...safeUpdates, ...(refreshTimestamp ? { timestamp: new Date() } : {}) };
            }),
            updatedAt: new Date(),
          }
        : null,
    }));
  },

  removeMessage: (messageId: string) => {
    set(state => ({
      currentSession: state.currentSession
        ? {
            ...state.currentSession,
            messages: state.currentSession.messages.filter(m => m.id !== messageId),
            updatedAt: new Date(),
          }
        : null,
    }));
  },

  setMessages: (messages: ChatMessage[]) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting messages:", messages.length);
    const safeMessages = messages.some(m => typeof m.content !== "string")
      ? messages.map(m => (typeof m.content === "string" ? m : { ...m, content: "" }))
      : messages;
    set(state => ({
      currentSession: state.currentSession
        ? {
            ...state.currentSession,
            messages: safeMessages,
            updatedAt: new Date(),
          }
        : null,
    }));
  },

  // State management
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  setPreparing: (isPreparing: boolean) => {
    set({ isPreparing });
  },

  setLoadingHistory: (isLoadingHistory: boolean) => {
    set({ isLoadingHistory });
  },

  setError: (error: Error | null) => {
    set({ error });
  },

  setLastRequest: (request: { message: string; workflowName?: string; workflowDescription?: string } | null) => {
    set({ lastRequest: request });
  },

  setPendingMessage: (message: string | null) => {
    const validated = typeof message === "string" || message === null ? message : null;
    set(state => ({
      pendingMessage: validated,
      // Increment id on every non-null set so the auto-send effect treats
      // re-clicks with the same message as new requests. See B3-3880.
      pendingMessageId: validated !== null ? state.pendingMessageId + 1 : state.pendingMessageId,
    }));
  },

  setPendingImages: (images: ChatImageAttachment[]) => {
    set({ pendingImages: Array.isArray(images) ? images : [] });
  },

  setPendingErrorBlock: (message: string | null) => {
    set({ pendingErrorBlock: message });
  },

  setCanvasPrefill: (message: string | null) => {
    set({ canvasPrefill: message });
  },

  setCanvasSubmitOverride: (fn: ((message: string) => void) | null) => {
    set({ canvasSubmitOverride: fn });
  },

  // Streaming actions
  setStreaming: (isStreaming: boolean) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting streaming:", isStreaming);
    set({ isStreaming });
  },

  addStreamingNode: (nodeId: string, node: StreamingNode) => {
    aiChatStoreDebug.log("🤖 [AI Store] Adding streaming node:", nodeId);
    set(state => {
      const newNodes = new Map(state.streamingNodes);
      newNodes.set(nodeId, node);

      // Prevent unbounded growth - remove oldest nodes if limit exceeded
      if (newNodes.size > AI_CHAT_LIMITS.MAX_STREAMING_NODES) {
        const firstKey = newNodes.keys().next().value;
        if (firstKey) {
          newNodes.delete(firstKey);
        }
      }

      return { streamingNodes: newNodes };
    });
  },

  clearStreamingNodes: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Clearing streaming nodes");
    set({ streamingNodes: new Map() });
  },

  appendStreamingConversation: (chunk: string) => {
    set(state => ({
      streamingConversation: state.streamingConversation + chunk,
    }));
  },

  clearStreamingConversation: () => {
    set({ streamingConversation: "" });
  },

  addLoadingNodeId: (nodeId: string) => {
    set(state => {
      const newSet = new Set(state.loadingNodeIds);
      newSet.add(nodeId);

      // Prevent unbounded growth - remove oldest entries if limit exceeded
      if (newSet.size > AI_CHAT_LIMITS.MAX_LOADING_NODE_IDS) {
        const firstValue = newSet.values().next().value;
        if (firstValue) {
          newSet.delete(firstValue);
        }
      }

      return { loadingNodeIds: newSet };
    });
  },

  removeLoadingNodeId: (nodeId: string) => {
    set(state => {
      const newSet = new Set(state.loadingNodeIds);
      newSet.delete(nodeId);
      return { loadingNodeIds: newSet };
    });
  },

  clearLoadingNodeIds: () => {
    set({ loadingNodeIds: new Set() });
  },

  addThinkingMessage: (message: string) => {
    aiChatStoreDebug.log("🤖 [AI Store] Adding thinking message:", message);
    set(state => ({
      thinkingMessages: [...state.thinkingMessages, message],
    }));
  },

  clearThinkingMessages: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Clearing thinking messages");
    set({ thinkingMessages: [] });
  },

  addToolCall: (toolCall: ToolCallStatus) => {
    aiChatStoreDebug.log("🤖 [AI Store] Adding tool call:", toolCall.toolName);
    set(state => ({
      toolCalls: [...state.toolCalls, toolCall],
    }));
  },

  updateToolCall: (toolName: string, updates: Partial<ToolCallStatus>) => {
    aiChatStoreDebug.log("🤖 [AI Store] Updating tool call:", toolName);
    set(state => {
      const index = state.toolCalls.findIndex(tc => tc.toolName === toolName && tc.status === "running");
      if (index === -1) return state;
      const newToolCalls = [...state.toolCalls];
      newToolCalls[index] = { ...newToolCalls[index], ...updates };
      return { toolCalls: newToolCalls };
    });
  },

  clearToolCalls: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Clearing tool calls");
    set({ toolCalls: [] });
  },

  setBraintrustTraceUrl: (url: string) => {
    set({ braintrustTraceUrl: url });
  },

  archiveToolCallHistory: (userMessage: string) => {
    const { toolCalls } = get();
    aiChatStoreDebug.log(
      "🤖 [AI Store] Archiving tool call history:",
      toolCalls.length,
      "calls for message:",
      userMessage.slice(0, 50),
    );
    set(state => ({
      toolCallHistory: [
        ...state.toolCallHistory,
        {
          userMessage,
          toolCalls: [...state.toolCalls],
          timestamp: new Date(),
        },
      ],
      toolCalls: [],
    }));
  },

  // Pending ask/plan block actions
  setHasPendingAskBlock: (hasPending: boolean) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting hasPendingAskBlock:", hasPending);
    set({ hasPendingAskBlock: hasPending });
  },

  setHasPendingPlanBlock: (hasPending: boolean) => {
    aiChatStoreDebug.log("🤖 [AI Store] Setting hasPendingPlanBlock:", hasPending);
    set({ hasPendingPlanBlock: hasPending });
  },

  setPendingTemplateIntercept: (intercept: PendingTemplateIntercept | null) => {
    set({ pendingTemplateIntercept: intercept });
  },

  // Agent mode actions
  setCurrentMode: mode => {
    set(state => {
      const sessionKey = state.currentSession?.workflowId ?? state.currentSession?.sessionId ?? null;
      if (mode && sessionKey) {
        return { currentMode: mode, sessionModes: { ...state.sessionModes, [sessionKey]: mode } };
      }
      return { currentMode: mode };
    });
  },

  setPreferredMode: mode => {
    set({ preferredMode: mode });
  },

  // Reconnection actions
  setRequestId: (id: string | null) => {
    set({ requestId: id });
  },

  updateLastEventIndex: (index: number) => {
    set(state => ({
      lastEventIndex: Math.max(state.lastEventIndex, index),
    }));
  },

  setReconnecting: (isReconnecting: boolean, attempt?: number) => {
    set({
      isReconnecting,
      reconnectAttempt: attempt ?? 0,
    });
  },

  // Reset
  reset: () => {
    aiChatStoreDebug.log("🤖 [AI Store] Resetting store");
    set({
      ...initialState,
      // Reset Maps and Sets to new instances
      streamingNodes: new Map(),
      loadingNodeIds: new Set(),
      toolCalls: [],
      toolCallHistory: [],
      braintrustTraceUrl: null,
      hasPendingAskBlock: false,
      hasPendingPlanBlock: false,
      pendingTemplateIntercept: null,
      requestId: null,
      lastEventIndex: -1,
      isReconnecting: false,
      reconnectAttempt: 0,
      sessionModes: {},
    });
  },
}));
